import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import { UserCircle, Check, Sparkles, Mic, Video, Users } from "lucide-react";

const CloneCreator = () => {
  const features = [
    { icon: UserCircle, title: "Photorealistic AI Clones", description: "Create a hyper-realistic digital twin of yourself or your team — trained on your face, voice, and brand persona." },
    { icon: Mic, title: "Voice Cloning", description: "Studio-grade voice cloning in any language. Your clone speaks like you, with your tone, pace, and personality." },
    { icon: Video, title: "Talking Avatar Videos", description: "Generate unlimited talking-head videos from a single script. Perfect for sales, training, onboarding, and outreach." },
    { icon: Sparkles, title: "Branded & Consistent", description: "Every clone is locked to your brand voice, wardrobe, and visual identity so your message stays on-brand at scale." },
  ];

  const includes = [
    "Custom AI clone (face + voice) trained on your media",
    "Unlimited script-to-video generations",
    "Multilingual voiceovers and lip-sync",
    "Brand kit, backgrounds, and templates included",
    "Priority rendering and human QA review",
  ];

  const perfectFor = [
    "Founders and executives scaling their personal brand",
    "Sales teams sending personalized video outreach",
    "Coaches, creators, and consultants producing daily content",
    "Enterprises deploying internal training and onboarding videos",
  ];

  return (
    <div className="min-h-screen bg-background">
      <SEO
        title="AI Clone Creator — Neil AI Global Solutions"
        description="Create a photorealistic AI clone of yourself with voice cloning and unlimited talking-avatar video generation. Built for founders, sales teams, and brands."
        keywords="AI clone, digital twin, voice cloning, avatar video, talking head AI, personal brand AI"
      />
      <Navigation />

      <main className="pt-40 pb-16">
        <section className="container mx-auto px-4 py-16 text-center">
          <Badge className="mb-4 text-lg px-6 py-2 bg-gradient-to-r from-primary to-secondary">
            AI Clone Creator
          </Badge>
          <h1 className="text-4xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-primary via-accent to-secondary bg-clip-text text-transparent">
            Build Your AI Clone. Scale Yourself Infinitely.
          </h1>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto mb-8">
            Neil AI builds a photorealistic digital twin of you — face, voice, and personality — so you can produce unlimited videos, training, and outreach without ever stepping in front of a camera again.
          </p>
          <div className="flex gap-4 justify-center flex-wrap mb-8">
            <Button size="lg" className="text-lg" asChild>
              <a href="https://api.leadconnectorhq.com/widget/bookings/neilaipudurobotics">Book a Clone Consultation</a>
            </Button>
            <Button size="lg" variant="outline" className="text-lg" asChild>
              <a href="/book">Request a Quote</a>
            </Button>
          </div>
        </section>

        <section className="container mx-auto px-4 py-16">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">What You Get</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, i) => {
              const Icon = feature.icon;
              return (
                <Card key={i} className="border-2 border-primary/20 hover:border-primary/40 transition-colors">
                  <CardHeader>
                    <div className="h-14 w-14 rounded-lg bg-gradient-to-br from-primary/10 to-secondary/10 flex items-center justify-center mb-4 mx-auto">
                      <Icon className="h-7 w-7 text-primary" />
                    </div>
                    <CardTitle className="text-lg text-center">{feature.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground text-center">{feature.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </section>

        <section className="container mx-auto px-4 py-16 bg-muted/30 rounded-2xl">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold mb-8 text-center">Your Plan Includes</h2>
            <div className="grid gap-4">
              {includes.map((item, i) => (
                <Card key={i} className="border-2 border-primary/20">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-3">
                      <Check className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
                      <p className="text-foreground font-medium text-lg">{item}</p>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        <section className="container mx-auto px-4 py-16">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold mb-8 text-center">Who It's Perfect For</h2>
            <div className="grid md:grid-cols-2 gap-4">
              {perfectFor.map((item, i) => (
                <Card key={i} className="border-2 border-primary/20 hover:border-primary/40 transition-colors">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-3">
                      <Users className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
                      <p className="text-foreground">{item}</p>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        <section className="container mx-auto px-4 py-16">
          <Card className="max-w-4xl mx-auto text-center border-4 border-primary/30 bg-gradient-to-br from-primary/5 to-secondary/5">
            <CardHeader>
              <CardTitle className="text-3xl md:text-4xl font-bold mb-4">
                Ready to Meet Your AI Clone?
              </CardTitle>
              <p className="text-xl text-muted-foreground">
                Book a free consultation and we'll show you exactly how your clone will look, sound, and perform.
              </p>
            </CardHeader>
            <CardContent>
              <div className="flex gap-4 justify-center flex-wrap">
                <Button size="lg" className="text-lg" asChild>
                  <a href="https://api.leadconnectorhq.com/widget/bookings/neilaipudurobotics">Book Free Consultation</a>
                </Button>
                <Button size="lg" variant="secondary" className="text-lg" asChild>
                  <a href="/seedance-video">Explore Seedance 2.0 Video →</a>
                </Button>
              </div>
            </CardContent>
          </Card>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default CloneCreator;
